// src/pages/Contact.jsx

import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { FaLinkedin } from 'react-icons/fa';
import { firestore } from '../firebase';
import GeometricDivider from '../components/GeometricDivider';
import useDocumentTitle from '../hooks/useDocumentTitle';
import './Contact.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OWNER_EMAIL = 'ariankazemi.dev@gmail.com';

const initialForm = { name: '', email: '', message: '' };

export default function Contact() {
  useDocumentTitle('Contact', 'Get in touch with Arian Kazemi.');

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Please enter your name.';
    if (!form.email.trim() || !EMAIL_RE.test(form.email)) next.email = 'Please enter a valid email.';
    if (!form.message.trim()) next.message = 'Please enter a message.';
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setStatus('submitting');
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();

    try {
      await Promise.all([
        addDoc(collection(firestore, 'contactMessages'), {
          name,
          email,
          message,
          createdAt: serverTimestamp(),
        }),
        // Picked up by the Firebase "Trigger Email" extension so submissions
        // land in the owner's inbox instead of only sitting in Firestore.
        addDoc(collection(firestore, 'mail'), {
          to: [OWNER_EMAIL],
          message: {
            subject: `Portfolio contact form: ${name}`,
            text: `From: ${name} <${email}>\n\n${message}`,
          },
        }),
      ]);
      setForm(initialForm);
      setStatus('success');
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <>
      <section className="hero">
        <div className="hero-content page-container">
          <h1>Contact</h1>
          <p className="tagline">Have a question or want to work together? Reach out.</p>
        </div>
      </section>

      <GeometricDivider />

      <section className="contact page-container">
        <div className="contact-grid">
          <form className="contact-form card" onSubmit={handleSubmit} noValidate>
            <div className="form-field">
              <label htmlFor="name">Name</label>
              <input
                id="name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? 'name-error' : undefined}
              />
              {errors.name && <span id="name-error" className="field-error">{errors.name}</span>}
            </div>

            <div className="form-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && <span id="email-error" className="field-error">{errors.email}</span>}
            </div>

            <div className="form-field">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                rows="5"
                value={form.message}
                onChange={handleChange}
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? 'message-error' : undefined}
              />
              {errors.message && <span id="message-error" className="field-error">{errors.message}</span>}
            </div>

            <button type="submit" className="btn primary" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'Sending…' : 'Send Message'}
            </button>

            <div role="status" aria-live="polite">
              {status === 'success' && (
                <p className="form-status success">Thanks — your message has been sent.</p>
              )}
              {status === 'error' && (
                <p className="form-status error">
                  Something went wrong. Please try again in a moment, or reach out on LinkedIn.
                </p>
              )}
            </div>
          </form>

          <div className="contact-direct card">
            <h2>Elsewhere</h2>
            <ul className="contact-direct-list">
              <li>
                <a href="https://linkedin.com/in/ariankazemi" target="_blank" rel="noopener noreferrer">
                  <FaLinkedin /> linkedin.com/in/ariankazemi
                </a>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <GeometricDivider />
    </>
  );
}
